import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import type { Day } from "../types";

interface DayInfoModalProps {
  day: Day;
  isOpen: boolean;
  onClose: () => void;
}

export const DayInfoModal: React.FC<DayInfoModalProps> = ({ day, isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleEscape);

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md"
          onClick={onClose}
        >
          {/* Backdrop wrapper to handle click-away correctly */}
          <div className="absolute inset-0" onClick={onClose} />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="w-full max-w-[400px] rounded-2xl border border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.85)] bg-[#1a1a1a]/95 flex flex-col overflow-hidden relative z-10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ambient Background Glow */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#22D3BA]/10 to-[#F7C4FF]/10 blur-2xl pointer-events-none" />

            {/* Header: Support-band's masking-tape-gradient */}
            <div className="masking-tape-gradient px-6 py-4 border-b border-white/10 flex justify-between items-center relative z-10 text-[#0F0F0F]">
              <div className="flex flex-col relative z-20">
                <h2 className="font-['Unbounded'] text-sm font-black uppercase tracking-wider">
                  {day.name}
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-[#0F0F0F]/70 hover:text-[#0F0F0F] font-bold p-1 relative z-20 transition-colors"
                aria-label="Bezárás"
              >
                ✕
              </button>
            </div>

            {/* Content Box: More Transparency */}
            <div className="bg-black/40 px-6 py-6 relative z-10 flex-1">
              <p className="text-sm leading-relaxed text-white font-medium whitespace-pre-wrap break-words">
                {day.description}
              </p>
            </div>

            {/* Footer with Close Button */}
            <div className="bg-black/20 px-6 py-4 border-t border-white/5 relative z-10">
              <button
                type="button"
                onClick={onClose}
                className="w-full bg-main text-[#222222] font-['Unbounded'] font-bold text-xs py-2.5 rounded-xl hover:brightness-110 active:scale-[0.98] transition-all uppercase tracking-wider"
              >
                Bezárás
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
